import { useEffect, useState } from "react";
import { Button, Flex, Pagination, Table, message, Typography, Tooltip } from "antd";
import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import { API, graphqlOperation } from "aws-amplify";
import Service from "../../../services";
import * as mutations from "../../../graphql/mutations";
import * as queries from "../../../graphql/queries";
import { SubmissionSources } from "../../../components/TouchstoneSubmissions/SubmissionSources";
import { TaskLogs } from "../../../components/TouchstoneTasks/TaskLogs";
import { TaskArtifacts } from "../../../components/TouchstoneTasks/TaskArtifacts";
import dayjs from "dayjs";
import { errorToString } from "../../../utils";
import { TOUCHSTONE_SUBMISSIONS_STATUS, TOUCHSTONE_TASKS_STATUS, TOUCHSTONE_TASKS_TYPES } from "../../../utils/touchstone";
import "./index.scss";

const { Title } = Typography;

const pageSizes = [20, 50, 100];

const TouchstoneTasks = () => {
  const [tasks, setTasks] = useState();
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalTasks, setTotalTasks] = useState(999999999);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: pageSizes[0],
  });
  const [tokensByPage, setTokensByPage] = useState({ 1: null });

  const fetchPage = async (page, pageSize, tokensByPage) => {
    setLoading(true);
    try {
      const token = tokensByPage[page];
      const result = await Service.listTouchstoneTasksPaginated({
        limit: pageSize,
        nextToken: token,
      });

      if (page > 1 && result.items.length === 0) {
        // we scrolled past the last page
        setTotalTasks((page - 1) * pageSize);
      } else {
        setTasks(result.items.map((item, idx) => ({ ...item, key: idx })));

        if (result.nextToken) {
          setTokensByPage((prev) => ({ ...prev, [page + 1]: result.nextToken }));
        } else {
          setTotalTasks(pageSize * page);
        }
      }
    } catch (error) {
      message.error(errorToString(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(1, pageSizes[0], { 1: null });
    const timer = setInterval(() => setShouldFetchData(true), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (shouldFetchData) {
      fetchPage(pagination.current, pagination.pageSize, tokensByPage);
      setShouldFetchData(false);
    }
  }, [shouldFetchData, pagination, tokensByPage]);

  const handleRetry = async (record) => {
    try {
      const promises = [
        API.graphql(
          graphqlOperation(mutations.updateTouchstoneTasks, {
            input: {
              submission_id: record.submission_id,
              task_id: record.task_id,
              status: TOUCHSTONE_TASKS_STATUS.QUEUED,
              resource: null,
              console_log_tail: null,
              console_log_url: null,
              ts_task_started: null,
              ts_task_finished: null,
              ts_training_started: null,
              ts_training_finished: null,
            },
          })
        ),
      ];

      const submissionResponse = await API.graphql(
        graphqlOperation(queries.listTouchstoneSubmissionsById, {
          submission_id: record.submission_id,
        })
      );
      const submissions = submissionResponse.data.listTouchstoneSubmissionsById.items;
      const submission = submissions.length > 0 ? submissions[0] : null;

      if (submission) {
        let submissionNewStatus = TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED;
        if (submission.status === TOUCHSTONE_SUBMISSIONS_STATUS.VALIDATION_ERROR) {
          submissionNewStatus = TOUCHSTONE_SUBMISSIONS_STATUS.VALIDATING;
        }
        promises.push(
          API.graphql(
            graphqlOperation(mutations.updateTouchstoneSubmissions, {
              input: {
                system: submission.system,
                ts_created: submission.ts_created,
                status: submissionNewStatus,
                ts_finished: null,
              },
            })
          )
        );
      }
      await Promise.all(promises);
      setShouldFetchData(true);
      message.success(`Relaunched task: ${record.task_id}`);
    } catch (error) {
      message.error(`Failed to relaunch task: ${errorToString(error)}`);
    }
  };

  const handleClone = async (record) => {
    try {
      let cloneTaskId;
      const match = record.task_id.match(/^(.*_c)(\d+)$/);
      if (match) {
        cloneTaskId = `${match[1]}${parseInt(match[2], 10) + 1}`;
      } else {
        cloneTaskId = `${record.task_id}_c0`;
      }
      const attributesToCopy = [
        "submission_id",
        "desired_resource",
        "config_file",
        "el_nigma_branch",
        "sources",
        "pred_interval",
        "fetched_data_end_to",
        "passthrough_args",
        "type",
        "gsi_pk",
      ];
      const clone = {
        ...Object.fromEntries(attributesToCopy.map((key) => [key, record[key]])),
        task_id: cloneTaskId,
        status: TOUCHSTONE_TASKS_STATUS.QUEUED,
        status_timestamp: Math.floor(Date.now() / 1000),
      };

      const submissionResponse = await API.graphql(
        graphqlOperation(queries.listTouchstoneSubmissionsById, {
          submission_id: record.submission_id,
        })
      );
      const submissions = submissionResponse.data.listTouchstoneSubmissionsById.items;
      const submission = submissions.length > 0 ? submissions[0] : null;

      const promises = [
        API.graphql(
          graphqlOperation(mutations.createTouchstoneTasks, {
            input: clone,
          })
        ),
      ];

      if (submission) {
        promises.push(
          API.graphql(
            graphqlOperation(mutations.updateTouchstoneSubmissions, {
              input: {
                system: submission.system,
                ts_created: submission.ts_created,
                status: TOUCHSTONE_SUBMISSIONS_STATUS.QUEUED,
                ts_finished: null,
              },
            })
          )
        );
      }

      await Promise.all(promises);
      setShouldFetchData(true);
      message.success(`Cloned task ${record.task_id} to ${clone.task_id}`);
    } catch (error) {
      message.error(`Failed to clone task: ${errorToString(error)}`);
    }
  };

  const renderDeploymentDetails = (_, record) => {
    const resource = JSON.parse(record.resource || record.desired_resource || "{}");
    if (resource.type === "ANY") {
      // don't expose the RunPod defaults embedde within, to avoid confusion. These are only used if the backend
      // decides to run the task on RunPod.
      delete resource.gpu_type;
      delete resource.cloud_type;
      delete resource.data_center_id;
    }
    if (resource.slots) {
      resource.slots = resource.slots.join();
    }
    return (
      <Tooltip
        title={
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {Object.getOwnPropertyNames(resource).map((n, idx) => (
              <li key={idx}>
                {n}: <span style={{ color: "lightgreen" }}>{resource[n]}</span>
              </li>
            ))}
          </ul>
        }
      >
        <Button type="link">Details</Button>
      </Tooltip>
    );
  };

  const isRetriable = (record) =>
    [TOUCHSTONE_TASKS_STATUS.FINISHED_CANCELED, TOUCHSTONE_TASKS_STATUS.FINISHED_ERROR].includes(record.status);

  const isClonable = (record) => record.type === TOUCHSTONE_TASKS_TYPES.TRAINING;

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize });
    setShouldFetchData(true);
  };

  if (!tasks) return null;

  const columns = [
    {
      title: "Submission ID",
      dataIndex: "submission_id",
      key: "submission_id",
      sorter: (a, b) => a.submission_id.localeCompare(b.submission_id),
    },
    {
      title: "Task ID",
      dataIndex: "task_id",
      key: "task_id",
      sorter: (a, b) => a.task_id.localeCompare(b.task_id),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => <span className={status}>{status}</span>,
    },
    {
      title: "Status Timestamp",
      dataIndex: "status_timestamp",
      key: "status_timestamp",
      sorter: (a, b) => new Date(a.status_timestamp) - new Date(b.status_timestamp),
      render: (status_timestamp) => (
        <Tooltip title={dayjs(status_timestamp * 1000).format("MMM D, YYYY HH:mm")}>{dayjs().to(dayjs(status_timestamp * 1000))}</Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Flex vertical>
          {isRetriable(record) && (
            <Button type="link" icon={<ReloadOutlined />} onClick={() => handleRetry(record)}>
              Retry
            </Button>
          )}
          {isClonable(record) && (
            <Button type="link" icon={<CopyOutlined />} onClick={() => handleClone(record)}>
              Clone
            </Button>
          )}
        </Flex>
      ),
    },
    {
      title: "Epochs",
      dataIndex: "epochs_completed",
      key: "epochs_completed",
      render: (epochs_completed) => {
        return epochs_completed || "";
      },
    },
    {
      title: "Sources",
      dataIndex: "sources",
      key: "sources",
      render: (sources) => <SubmissionSources sources={sources} />,
    },
    {
      title: "Deployment",
      key: "deployment_details",
      render: renderDeploymentDetails,
    },
    {
      title: "Configuration",
      key: "configuration_details",
      render: (_, record) => {
        const fields = [
          { label: "Config file", value: record.config_file || "n/a" },
          { label: "el-nigma branch", value: record.el_nigma_branch || "n/a" },
          { label: "Prediction interval", value: record.pred_interval || "n/a" },
          { label: "Passthrough args", value: record.passthrough_args || "n/a" },
          { label: "Fetched data end to", value: record.fetched_data_end_to || "n/a" },
        ];
        return (
          <Tooltip
            title={
              <ul style={{ listStyleType: "none", padding: 0 }}>
                {fields.map((f, idx) => (
                  <li key={idx}>
                    {f.label}: <span style={{ color: "lightgreen" }}>{f.value}</span>
                  </li>
                ))}
              </ul>
            }
          >
            <Button type="link">Details</Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Artifacts",
      dataIndex: "artifacts",
      key: "artifacts",
      render: (artifacts) => <TaskArtifacts artifacts={artifacts} />,
    },
    {
      title: "Logs",
      dataIndex: "console_log_tail",
      key: "logs",
      render: (_, record) => {
        return <TaskLogs console_log_tail={record.console_log_tail} console_log_url={record.console_log_url} />;
      },
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginTop: "20px 0" }}>
        All Tasks
      </Title>
      <Flex vertical gap={10}>
        <Table
          columns={columns}
          dataSource={tasks}
          style={{ marginTop: 20 }}
          pagination={false}
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
        <Pagination
          onChange={handlePaginationChange}
          current={pagination.current}
          pageSizeOptions={pageSizes}
          defaultPageSize={pageSizes[0]}
          showSizeChanger={true}
          total={totalTasks}
        />
      </Flex>
      <style>{`
        .${TOUCHSTONE_TASKS_STATUS.QUEUED} {
          color: dodgerblue;
        }
        .${TOUCHSTONE_TASKS_STATUS.DEPLOYING} {
          color: cornflowerblue;
        }
        .${TOUCHSTONE_TASKS_STATUS.PREPARING} {
          color: goldenrod;
        }
        .${TOUCHSTONE_TASKS_STATUS.PREPARED} {
          color: darkgoldenrod;
        }
        .${TOUCHSTONE_TASKS_STATUS.TRAINING} {
          color: lightcoral;
        }
        .${TOUCHSTONE_TASKS_STATUS.SUCCESS} {
          color: green;
        }
        .${TOUCHSTONE_TASKS_STATUS.ERROR} {
          color: red;
        }
        .${TOUCHSTONE_TASKS_STATUS.FINISHED_SUCCESS} {
          color: forestgreen;
        }
        .${TOUCHSTONE_TASKS_STATUS.FINISHED_ERROR} {
          color: firebrick;
        }
        .${TOUCHSTONE_TASKS_STATUS.PENDING_CANCEL} {
          color: orange;
        }
        .${TOUCHSTONE_TASKS_STATUS.FINISHED_CANCELED} {
          color: purple;
        }
      `}</style>
    </div>
  );
};

export default TouchstoneTasks;
